/**
 * Reactions and raised hands, carried over the room's chat relay.
 *
 * The signaling protocol has one broadcast channel, chat, and adding a message
 * type means changing the server, the protocol crate and every client at once.
 * Until that is worth doing, a reaction is a chat line whose text starts with an
 * invisible marker, and every client decodes it here. A client that predates
 * this shows a short odd line instead of breaking.
 */

export const REACTIONS = ['heart', 'cheer', 'laugh', 'fire', 'sad'] as const
export type Reaction = (typeof REACTIONS)[number]

const MARK = '⁣sofa:'

export type Social =
  | { kind: 'text'; text: string }
  | { kind: 'react'; reaction: Reaction }
  | { kind: 'hand'; up: boolean }

export function encodeReaction(reaction: Reaction): string {
  return `${MARK}react:${reaction}`
}

export function encodeHand(up: boolean): string {
  return `${MARK}hand:${up ? 'up' : 'down'}`
}

export function decode(text: string): Social {
  if (!text.startsWith(MARK)) return { kind: 'text', text }
  const [kind, value] = text.slice(MARK.length).split(':')
  if (kind === 'react' && (REACTIONS as readonly string[]).includes(value)) {
    return { kind: 'react', reaction: value as Reaction }
  }
  if (kind === 'hand' && (value === 'up' || value === 'down')) {
    return { kind: 'hand', up: value === 'up' }
  }
  return { kind: 'text', text }
}

/** Same name, same colour, on every screen. */
export function tone(name: string): number {
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  return hash % 4
}
